// @flow
import { connect } from 'react-redux';
import { get } from 'lodash';

import { getKids, getAdults, tripInformationSelector } from 'Store/trip-information/storeHelper';
import { endLoading, startLoading } from 'Store/global-loader/globalLoader.action';
import { getDepartureCity, getTripDuration } from 'Store/trip-criteria/tripCriteria.selector';
import { getArrivalDate, getDepartureDate } from 'Store/trip-date-criteria/storeHelper';
import { type BookingEngineEntry } from 'Store/booking/constants';
import {
  getProposals,
  getCurrentResort,
  getSelectedProposalId,
  getDetailedAttendees,
  getSuggestions,
} from 'Store/booking/booking.selector';
import {
  clearProposals,
  fetchProposalsAndRedirect,
  prepareBe,
} from 'Store/booking/proposals/proposals.action';
import {
  selectProposal,
  clearSelectedProposal,
} from 'Store/booking/selected-proposal-id/selectedProposalId.action';
import { setAttendees } from 'Store/trip-information/adults/adults.action';
import { updateTripDateCriteria } from 'Store/trip-date-criteria/tripDateCriteria.action';
import { setDepartureDate } from 'Store/trip-criteria/departure-date/departureDate.action';
import { DEFAULT_ADULTS_NB } from 'Store/trip-information/adults/constants';
import { setCms } from 'Store/cms/cms.actions';

import { DEFAULT_TRIP_DURATION } from 'Store/trip-criteria/duration/duration.constants';
import { getResortInitialKids } from 'Utils/trip-information/tripInformationUtils';
import { QUERY_TRANSPORT } from 'Utils/uri/constants';

import BookingEngineForm from './BookingEngineForm.jsx';

function getValue<T>(value: ?T, initialValue: T): ?T {
  return !value && initialValue ? initialValue : value;
}

function getQueryParamValue(name: string, location) {
  return get(location.queryParams, name);
}

type ComponentProps = {
  bookingCriteria?: {
    initialNumberOfChildren?: number,
    initialNumberOfAdults?: number,
    initialDuration?: number,
    initialTransport?: string,
  },
  location: RouterLocation<{}, *>,
  resort: $Shape<Resort>,
};

type ContainerProps = {
  adultsNumber: number,
  arrivalDate: ?number, // parseInt de "YYYYMMDD"
  bookingEngineEntry: BookingEngineEntry,
  departureDate: ?number, // parseInt de "YYYYMMDD"
  departureCity: ?string,
  duration: number,
  kids: Array<*>,
  proposals: Array<Proposal>,
};

function getValidDepartureCity(state: ReduxState, props: ComponentProps): ?string {
  const availableTransports = get(props, 'bookingCriteria.departureCities') || [];
  const transportIATACodes = availableTransports.map(t => t.value);
  const departureCity =
    getDepartureCity(state) || getQueryParamValue(QUERY_TRANSPORT, props.location);

  return transportIATACodes.includes(departureCity)
    ? departureCity
    : get(props, 'bookingCriteria.initialTransport');
}

export function mapStateToProps(state: ReduxState, props: ComponentProps): ContainerProps {
  const kids = getKids(state) || [];
  const adults = (getAdults(state) || []).length;
  const duration = getTripDuration(state);
  const departureCity = getValidDepartureCity(state, props);
  const departureDate = getDepartureDate(state);

  return {
    adultsNumber:
      getValue(adults, get(props, 'bookingCriteria.initialNumberOfAdults')) || DEFAULT_ADULTS_NB,
    arrivalDate: getArrivalDate(state),
    bookingEngineEntry: state.booking.bookingEngineEntry,
    departureDate,
    departureCity: getValue(departureCity, get(props, 'bookingCriteria.initialTransport')),
    duration:
      getValue(duration, get(props, 'bookingCriteria.initialDuration')) || DEFAULT_TRIP_DURATION,
    kids: getResortInitialKids(
      kids,
      get(props, 'bookingCriteria.initialNumberOfChildren'),
      get(props, 'resort.adultOnly'),
    ),
    proposals: getProposals(state),
    selectedProposalId: getSelectedProposalId(state),
    resort: {
      ...props.resort,
      ...getCurrentResort(state),
    },
    attendees: getDetailedAttendees(state),
    tripInformation: tripInformationSelector(state),
    suggestions: getSuggestions(state),
  };
}

export const mapDispatchToProps = {
  clearProposals,
  endLoading,
  startLoading,
  updateTripDateCriteria,
  setDepartureDate,
  clearSelectedProposal,
  selectProposal,
  fetchProposalsAndRedirect,
  setCms,
  setAttendees,
  prepareBe,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(BookingEngineForm);
